import { Transfer } from "antd";
import Axios from "axios";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../../utils/apiMessageHelper";
import { NavBar } from "./../navBar";
import { SideBar } from "./../sideBar";
import rutineAvatar from "../../assets/images/user/rutine-avatar.png";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import useStoredPermissions from "../useStoredPermissions";

export const DetailRutine = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const hasPermissions =
    permissions.includes("Super User") ||
    permissions.includes("Read Video") ||
    permissions.includes("Read Routine");

  //> Constants
  const [data, setData] = useState([]);
  const { id } = useParams();

  const [mockData, setMockData] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);

  // Contexts
  const { notify } = useNotification();
  const { clientId } = useAuth();

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  const handleChange = (newTargetKeys) => {
    setTargetKeys(newTargetKeys);
  };

  //> STATES

  const [currentRutine, setCurrentRutine] = useState([]);

  //> API CALLS
  async function getData() {
    try {
      const tempTargetKeys = [];
      const tempMockData = [];

      // Use currentRutine.idClient if available, otherwise use clientId for non-super admins
      const clientIdToUse =
        currentRutine?.idClient || (!isSuperAdmin ? clientId : null);

      const url = clientIdToUse
        ? `${process.env.REACT_APP_API_URL}/api/v1/Video/list/${clientIdToUse}`
        : `${process.env.REACT_APP_API_URL}/api/v1/Video/list`;

      const resAll = await Axios.get(url);
      const resSelected = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Video/ByRoutine/${id}`,
      );

      if (resAll.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("routines.noVideosMessage"),
        }));
      }

      resAll.data.responseObject.forEach((i) => {
        const data = {
          key: i.idVideo,
          title: i.name,
          description: i.description,
        };
        tempMockData.push(data);
      });

      resSelected.data.responseObject.forEach((i) => {
        if (i.id_video !== null && i.id_video !== undefined) {
          tempTargetKeys.push(i.id_video);
        }
      });

      setTargetKeys(tempTargetKeys);
      setMockData(tempMockData);
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  }

  async function getCurrentRutine() {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Routine/${id}`,
      );

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("routines.noClientsMessage"),
        }));
      }
      setCurrentRutine(res.data.responseObject);
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  }

  async function updateVideosOnRutine() {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/Routine/videos/${id}`,
        targetKeys,
      );

      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) || t("routines.videosAssignedSuccess");
        notify(message, { type: "success" });
      }

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("routines.noClientsMessage"),
        }));
      }
      setCurrentRutine(res.data.responseObject);
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }

    getCurrentRutine();
  }

  //> lifecycles
  useEffect(() => {
    getCurrentRutine();
  }, []);

  useEffect(() => {
    if (currentRutine?.idRoutine) {
      getData();
    }
  }, [currentRutine]);

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <div>
              <Button
                id="btnLarge"
                variant="primary"
                onClick={updateVideosOnRutine}
                disabled={!isSuperAdmin && currentRutine?.idClient !== clientId}
              >
                {t("routines.updateRoutineVideos")}
              </Button>
            </div>
          </Row>
          <Row>
            <Col xs={12} md={8}>
              <Card>
                <Card.Header>
                  <Card.Title as="h5">
                    {t("routines.detailRoutineTitle")} {currentRutine?.name}
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="transfer-responsive">
                    <Transfer
                      dataSource={mockData}
                      showSearch
                      listStyle={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 600, // Max height to prevent overflow
                      }}
                      operations={[
                        t("common.add").toLowerCase(),
                        t("routines.removeOperation"),
                      ]}
                      targetKeys={targetKeys}
                      onChange={handleChange}
                      render={(item) => `${item.title}`}
                      titles={[
                        t("routines.availableVideos"),
                        t("routines.addedVideos"),
                      ]}
                      locale={{
                        itemUnit: t("routines.videoUnit"),
                        itemsUnit: t("routines.videosUnit"),
                        notFoundContent: t("routines.noVideosFound"),
                        searchPlaceholder: t("routines.searchVideos"),
                      }}
                      disabled={
                        !isSuperAdmin && currentRutine?.idClient !== clientId
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card>
                <Card.Body className="p-0">
                  <div>
                    <div className="text-center project-main">
                      <img
                        className="img-fluid rounded-circle"
                        src={rutineAvatar}
                        alt="dashboard-user"
                      />
                      {/* Name */}
                      <div className="row m-t-30">
                        <div className="col-4">
                          <span>{t("routines.nameLabel")}:</span>
                        </div>
                        <div className="col-8">
                          <h6>{currentRutine?.name}</h6>
                        </div>
                      </div>
                      {/* Description */}
                      <div className="row m-t-30">
                        <div className="col-4">
                          <span>{t("routines.descriptionLabel")}:</span>
                        </div>
                        <div className="col-8">
                          <h6>{currentRutine?.description}</h6>
                        </div>
                      </div>
                    </div>
                    <div className="border-top" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Container>
    </React.Fragment>
  );
};
